/**
 * Permission Enforcement Service
 * 
 * This service provides ACTUAL enforcement of permissions, not just UI hiding.
 * It throws errors when permissions are denied, which can be caught and displayed.
 * 
 * Usage:
 * 1. Import the enforcement functions
 * 2. Call them before performing protected actions
 * 3. Handle PermissionDeniedError appropriately
 */

import type { PermissionAction, PermissionScope, ScopeKind } from '../types/prioritization/permissions';
import { PERMISSION_DEFINITIONS } from '../types/prioritization/permissions';
import type { ActorRole } from '../types/prioritization/roles';
import { ACTOR_ROLE_PERMISSIONS } from '../types/prioritization/rolePermissionMapping';
import { getActionsGrantedByCapability, isCapabilityId } from '../types/prioritization/capabilityIds';
import { getSupabaseClient } from './supabaseClient';
import { useUserStore } from '../stores/prioritization/userStore';

// ============================================================================
// Error Types
// ============================================================================

/**
 * Error thrown when a permission check fails
 */
export class PermissionDeniedError extends Error {
  public readonly action: PermissionAction;
  public readonly scope?: PermissionScope;
  public readonly reason: string;
  
  constructor(action: PermissionAction, scope?: PermissionScope, reason?: string) {
    const scopeInfo = scope ? ` at ${scope.kind}:${scope.id}` : '';
    const message = reason || `Permission denied: ${action}${scopeInfo}`;
    super(message);
    this.name = 'PermissionDeniedError';
    this.action = action;
    this.scope = scope;
    this.reason = reason || `You don't have permission to perform this action: ${action}`;
  }
}

// ============================================================================
// Permission Resolution
// ============================================================================

/** All permission actions in the system. Used to grant full permissions to workspace/tenant/platform admins. */
const ALL_PERMISSION_ACTIONS = Object.keys(PERMISSION_DEFINITIONS) as PermissionAction[];

/**
 * If the current user has an assigned admin role (workspace/tenant/platform), return all permissions
 * for the scope they administer, so admins effectively have full access within that scope.
 */
function getProcessPermissionsForAssignedAdmins(): Set<PermissionAction> {
  try {
    const assignedRoleNames = useUserStore.getState().assignedRoleNames ?? [];
    const hasAdmin =
      assignedRoleNames.includes('workspace-admin') ||
      assignedRoleNames.includes('tenant-admin') ||
      assignedRoleNames.includes('platform-admin') ||
      assignedRoleNames.includes('system-admin');
    return hasAdmin ? new Set(ALL_PERMISSION_ACTIONS) : new Set();
  } catch {
    return new Set();
  }
}

/**
 * User permission context - resolved from database
 */
export interface UserPermissionContext {
  userId: string;
  
  // Direct role (from role selector or database)
  primaryRole: ActorRole;
  
  // All permissions from all sources
  effectivePermissions: Set<PermissionAction>;
  
  // Scoped permissions (scope id -> permissions)
  scopedPermissions: Map<string, Set<PermissionAction>>;
  
  // Group memberships
  groupIds: string[];
  
  // When this context was loaded
  loadedAt: Date;
}

let currentUserContext: UserPermissionContext | null = null;

/**
 * What one role_permissions row grants, as the actions the app asks about.
 *
 * Roles in the database hold capability codes ("ITEM_CRUD", "SESSION_RUN");
 * screens ask for actions ("items.edit", "session.facilitate"). Without the
 * translation a person whose roles come from the database — every Item Manager
 * and Session Facilitator assigned in a workspace — was checked as if they held
 * only their selected role, and the controls their role grants stayed hidden
 * (found 2026-10-14 on the items list's bulk bar). The same map the policy
 * resolver uses (permissionResolver.ts) does the translating, so the two agree.
 * The raw value is kept too, for a row that already names an action.
 */
export function permissionActionsForRolePermission(permission: string): PermissionAction[] {
  const actions = new Set<PermissionAction>([permission as PermissionAction]);
  if (isCapabilityId(permission)) getActionsGrantedByCapability(permission).forEach((a) => actions.add(a));
  return [...actions];
}

/**
 * Load the current user's permission context from the database
 * 
 * IMPORTANT: This function is ADDITIVE. It will:
 * 1. Preserve any existing permissions in the cache (if same user)
 * 2. Add permissions from the primary role
 * 3. Add permissions from all direct role assignments
 * 4. Add permissions from all group role assignments
 * 
 * A user can NEVER lose permissions - they only accumulate.
 */
export async function loadUserPermissionContext(
  userId: string,
  primaryRole: ActorRole
): Promise<UserPermissionContext> {
  const supabase = getSupabaseClient();
  
  // Start with existing permissions if we have a cached context for this user
  // This ensures permissions are truly additive
  const existingPermissions = (currentUserContext && currentUserContext.userId === userId)
    ? new Set(currentUserContext.effectivePermissions)
    : new Set<PermissionAction>();
  
  // Get user's group memberships
  const { data: memberships } = await supabase
    .from('memberships')
    .select('group_id')
    .eq('user_id', userId);
  
  const groupIds = (memberships || []).map(m => m.group_id);
  
  // Get role assignments for user directly
  const { data: userRoleAssignments } = await supabase
    .from('role_assignments')
    .select('role_id, scope_kind, scope_id')
    .eq('subject_type', 'user')
    .eq('subject_id', userId);
  
  // Get role assignments for user's groups
  const { data: groupRoleAssignments } = groupIds.length > 0
    ? await supabase
        .from('role_assignments')
        .select('role_id, scope_kind, scope_id')
        .eq('subject_type', 'group')
        .in('subject_id', groupIds)
    : { data: [] };
  
  // Get all role permissions from database
  const allRoleIds = new Set<string>();
  [...(userRoleAssignments || []), ...(groupRoleAssignments || [])].forEach(ra => {
    allRoleIds.add(ra.role_id);
  });
  
  const { data: rolePermissions } = allRoleIds.size > 0
    ? await supabase
        .from('role_permissions')
        .select('role_id, permission')
        .in('role_id', Array.from(allRoleIds))
    : { data: [] };
  
  // Build permission sets by role
  const permissionsByRole = new Map<string, Set<PermissionAction>>();
  (rolePermissions || []).forEach(rp => {
    if (!permissionsByRole.has(rp.role_id)) {
      permissionsByRole.set(rp.role_id, new Set());
    }
    permissionActionsForRolePermission(rp.permission).forEach(a => permissionsByRole.get(rp.role_id)!.add(a));
  });
  
  // Build effective permissions - START with existing permissions (additive!)
  const effectivePermissions = existingPermissions;
  
  // Add permissions from primary role (the role selected in the UI)
  const primaryRolePermissions = ACTOR_ROLE_PERMISSIONS[primaryRole] || [];
  primaryRolePermissions.forEach(p => effectivePermissions.add(p));
  
  // Add permissions from database role assignments
  [...(userRoleAssignments || []), ...(groupRoleAssignments || [])].forEach(ra => {
    const rolePerms = permissionsByRole.get(ra.role_id);
    if (rolePerms) {
      rolePerms.forEach(p => effectivePermissions.add(p));
    }
  });

  // Add process.* for workspace/tenant/platform admins (align UI with policy engine)
  getProcessPermissionsForAssignedAdmins().forEach(p => effectivePermissions.add(p));

  // Build scoped permissions
  const scopedPermissions = new Map<string, Set<PermissionAction>>();
  [...(userRoleAssignments || []), ...(groupRoleAssignments || [])].forEach(ra => {
    const scopeKey = `${ra.scope_kind}:${ra.scope_id}`;
    if (!scopedPermissions.has(scopeKey)) {
      scopedPermissions.set(scopeKey, new Set());
    }
    const rolePerms = permissionsByRole.get(ra.role_id);
    if (rolePerms) {
      rolePerms.forEach(p => scopedPermissions.get(scopeKey)!.add(p));
    }
  });
  
  const context: UserPermissionContext = {
    userId,
    primaryRole,
    effectivePermissions,
    scopedPermissions,
    groupIds,
    loadedAt: new Date(),
  };
  
  currentUserContext = context;
  return context;
}

/**
 * Get the current user context (or create one from role)
 * 
 * IMPORTANT: Permissions are ADDITIVE. Users accumulate permissions from:
 * 1. The selected role's default permissions (ACTOR_ROLE_PERMISSIONS)
 * 2. Direct role assignments from the database
 * 3. Role assignments inherited through group memberships
 * 
 * A user can NEVER lose permissions by gaining a new role - they only gain more.
 */
export function getCurrentUserContext(
  userId: string,
  currentRole: ActorRole
): UserPermissionContext {
  // If we have a cached context for this user, use it and ADD the role's permissions
  if (currentUserContext && currentUserContext.userId === userId) {
    // Always ADD permissions from the selected role (never replace)
    const primaryRolePermissions = ACTOR_ROLE_PERMISSIONS[currentRole] || [];
    primaryRolePermissions.forEach(p => currentUserContext!.effectivePermissions.add(p));
    getProcessPermissionsForAssignedAdmins().forEach(p => currentUserContext!.effectivePermissions.add(p));
    currentUserContext.primaryRole = currentRole;
    return currentUserContext;
  }

  // Create a basic context from the current role
  // Note: This is a fallback - ideally loadUserPermissionContext should be called first
  const effectivePermissions = new Set<PermissionAction>(
    ACTOR_ROLE_PERMISSIONS[currentRole] || []
  );
  getProcessPermissionsForAssignedAdmins().forEach(p => effectivePermissions.add(p));

  return {
    userId,
    primaryRole: currentRole,
    effectivePermissions,
    scopedPermissions: new Map(),
    groupIds: [],
    loadedAt: new Date(),
  };
}

/**
 * Clear the cached user context
 */
export function clearUserContext(): void {
  currentUserContext = null;
}

// ============================================================================
// Permission Checking
// ============================================================================

/**
 * Check if the current user has a specific permission
 * 
 * @param action - The permission action to check
 * @param scope - Optional scope to check at
 * @param context - User permission context
 * @returns true if permission is granted
 */
export function hasPermission(
  action: PermissionAction,
  context: UserPermissionContext,
  scope?: PermissionScope
): boolean {
  // Check scoped permissions first if scope is provided
  if (scope) {
    const scopeKey = `${scope.kind}:${scope.id}`;
    const scopedPerms = context.scopedPermissions.get(scopeKey);
    if (scopedPerms && scopedPerms.has(action)) {
      return true;
    }
    
    // Walk up the scope chain
    if (scope.parent) {
      return hasPermission(action, context, scope.parent);
    }
  }
  
  // Check global/effective permissions
  return context.effectivePermissions.has(action);
}

/**
 * Check if the current user has any of the specified permissions
 */
export function hasAnyPermission(
  actions: PermissionAction[],
  context: UserPermissionContext,
  scope?: PermissionScope
): boolean {
  return actions.some(action => hasPermission(action, context, scope));
}

/**
 * Check if the current user has all of the specified permissions
 */
export function hasAllPermissions(
  actions: PermissionAction[],
  context: UserPermissionContext,
  scope?: PermissionScope
): boolean {
  return actions.every(action => hasPermission(action, context, scope));
}

// ============================================================================
// Permission Enforcement
// ============================================================================

/**
 * Require a specific permission - throws if not granted
 * 
 * @param action - The permission action required
 * @param context - User permission context
 * @param scope - Optional scope
 * @throws PermissionDeniedError if permission is not granted
 */
export function requirePermission(
  action: PermissionAction,
  context: UserPermissionContext,
  scope?: PermissionScope
): void {
  if (!hasPermission(action, context, scope)) {
    throw new PermissionDeniedError(action, scope);
  }
}

/**
 * Require any of the specified permissions - throws if none granted
 */
export function requireAnyPermission(
  actions: PermissionAction[],
  context: UserPermissionContext,
  scope?: PermissionScope
): void {
  if (!hasAnyPermission(actions, context, scope)) {
    throw new PermissionDeniedError(
      actions[0],
      scope,
      `You need one of these permissions: ${actions.join(', ')}`
    );
  }
}

/**
 * Require all of the specified permissions - throws if any missing
 */
export function requireAllPermissions(
  actions: PermissionAction[],
  context: UserPermissionContext,
  scope?: PermissionScope
): void {
  const missing = actions.filter(a => !hasPermission(a, context, scope));
  if (missing.length > 0) {
    throw new PermissionDeniedError(
      missing[0],
      scope,
      `You're missing these permissions: ${missing.join(', ')}`
    );
  }
}

// ============================================================================
// Convenience Functions (using userStore context)
// ============================================================================

/**
 * Get the current permission context from the user store
 */
export function getStoreContext(): UserPermissionContext {
  const state = useUserStore.getState();
  return getCurrentUserContext(state.userId, state.currentRole);
}

/**
 * Check permission using the current user from store
 */
export function canDo(action: PermissionAction, scope?: PermissionScope): boolean {
  const context = getStoreContext();
  return hasPermission(action, context, scope);
}

/**
 * Require permission using the current user from store
 * @throws PermissionDeniedError
 */
export function mustHave(action: PermissionAction, scope?: PermissionScope): void {
  const context = getStoreContext();
  requirePermission(action, context, scope);
}

/**
 * Create a guarded action that checks permissions before executing
 */
export function guardedAction<T extends (...args: never[]) => unknown>(
  requiredPermissions: PermissionAction | PermissionAction[],
  action: T,
  scope?: PermissionScope
): T {
  const permissions = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
  
  return ((...args: Parameters<T>) => {
    const context = getStoreContext();
    requireAllPermissions(permissions, context, scope);
    return action(...args);
  }) as T;
}

// ============================================================================
// React Hook for Enforcement
// ============================================================================

/**
 * Hook result for permission enforcement
 */
export interface UsePermissionEnforcementResult {
  /** Check if user has permission */
  can: (action: PermissionAction, scope?: PermissionScope) => boolean;
  /** Check if user has any of the permissions */
  canAny: (actions: PermissionAction[]) => boolean;
  /** Check if user has all of the permissions */
  canAll: (actions: PermissionAction[]) => boolean;
  /** Require permission - throws if not granted */
  require: (action: PermissionAction, scope?: PermissionScope) => void;
  /** Wrap an action with permission check */
  guard: <T extends (...args: never[]) => unknown>(
    permissions: PermissionAction | PermissionAction[],
    action: T
  ) => T;
  /** The user's permission context */
  context: UserPermissionContext;
  /** Whether permissions are loaded from database */
  isLoaded: boolean;
}

// Export types
export type { PermissionAction, PermissionScope, ScopeKind };
